import { useQuery } from '@tanstack/react-query'
import { t } from '@stokr/components-library'
import type { Project } from 'models/project'
import { useStoProject } from './useStoProject'
import { stoQueries } from './queries/stoQueries'
import { resolveSectionState, type SectionState } from 'models/stoSection'
import { isTabDeclaredVisible, pickContent, StoContentTypes, type OverviewContent } from 'models/projectContent'

export interface ImportantConsiderationItem {
  title: string
  /** Rich text (HTML) authored in back-office. */
  text: string
}

/** Editor leftovers anywhere in the text (`<p><strong><br></strong></p>`): a blank line each. */
const EMPTY_PARAGRAPH = /<p>(\s|&nbsp;|<br\s*\/?>|<\/?(strong|b|em|i|u|span)\b[^>]*>)*<\/p>/gi

const withoutEmptyParagraphs = (html: string): string => html.replace(EMPTY_PARAGRAPH, '').trim()

/** Same rule as the legacy block: a Tax ID row is appended when the project requires one. */
export const toImportantConsiderationItems = (
  overview: OverviewContent | undefined,
  project: Pick<Project, 'requiresTaxId'> | undefined,
): ImportantConsiderationItem[] => {
  const items = (overview?.importantConsiderations ?? [])
    .map((item) => ({ title: item.title?.trim() ?? '', text: withoutEmptyParagraphs(item.text ?? '') }))
    .filter((item) => item.title && item.text)

  if (project?.requiresTaxId) {
    items.push({
      title: t('stoPage.importantConsiderations.taxIdTitle'),
      text: t('riskConsiderations.taxIdDisclosure'),
    })
  }

  return items
}

export function useImportantConsiderations(): SectionState<ImportantConsiderationItem[]> {
  const { project, projectId } = useStoProject()
  const query = useQuery(stoQueries.content(projectId))
  const overview = pickContent<OverviewContent>(query.data, StoContentTypes.OVERVIEW)

  return resolveSectionState({
    declaredVisible: isTabDeclaredVisible(overview),
    isPending: query.isPending,
    isError: query.isError,
    data: toImportantConsiderationItems(overview, project),
    isEmpty: (items) => items.length === 0,
  })
}
